<template>
  <div ref="root" class="sp-radio-card-group" role="radiogroup" :aria-label="ariaLabel" part="base">
    <section v-for="group in normalizedGroups" :key="group.value" class="sp-radio-card-group__group" part="group">
      <header v-if="!group.standalone" class="sp-radio-card-group__header">
        <p class="sp-radio-card-group__group-name" part="group-name">{{ group.text }}</p>
        <p v-if="group.description" class="sp-radio-card-group__group-description" part="group-description">
          {{ group.description }}
        </p>
      </header>

      <div class="sp-radio-card-group__options">
        <button
          v-for="option in group.options"
          :key="option.value"
          type="button"
          class="sp-radio-card-group__option"
          :class="optionClass(option.value)"
          role="radio"
          :aria-checked="ariaChecked(option.value)"
          :tabindex="optionTabindex(option.value)"
          part="option"
          @click="selectOption(option.value)"
          @keydown="handleOptionKeydown($event, option.value)"
        >
          <span class="sp-radio-card-group__control" aria-hidden="true">
            <sp-icon v-if="isSelected(option.value)" class="sp-radio-card-group__check" name="check-filled"></sp-icon>
          </span>
          <span class="sp-radio-card-group__option-label" part="option-label">{{ option.text }}</span>
        </button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { props as radioProps } from "./props.js";
import { computed, useTemplateRef } from "vue";
import SpIcon from "#ce/components/SpIcon/SpIcon.ce.vue";

const props = defineProps(radioProps);

const emit = defineEmits(["update-selected"]);

const root = useTemplateRef("root");

// Normalize each group's children into a flat `options` array. A childless group
// that carries its own value is rendered as a single standalone radio-card: it is
// flagged `standalone` so the template omits the group header, otherwise its label
// would be duplicated as both the heading and the lone option (e.g. "Self pay").
const normalizedGroups = computed(() =>
  (props.groups ?? []).map((group) => {
    const hasChildren = Array.isArray(group.children) && group.children.length > 0;
    return {
      ...group,
      options: hasChildren ? group.children : [group],
      standalone: !hasChildren,
    };
  }),
);

// Flat list of every selectable value, in render order — used for roving-tabindex
// and arrow-key traversal across the whole radiogroup.
const optionValues = computed(() => normalizedGroups.value.flatMap((group) => group.options.map((o) => o.value)));

function isSelected(value) {
  return value != null && value === props.modelValue;
}

function optionClass(value) {
  const modifiers = {};
  if (isSelected(value)) {
    modifiers["--is-selected"] = true;
  }
  return modifiers;
}

function ariaChecked(value) {
  return isSelected(value) ? "true" : "false";
}

// Roving tabindex: the selected option is the single tab stop; when nothing is
// selected the first option is the tab stop so the group is keyboard-reachable.
function optionTabindex(value) {
  if (props.modelValue != null) {
    return isSelected(value) ? 0 : -1;
  }
  return optionValues.value[0] === value ? 0 : -1;
}

function selectOption(value) {
  if (isSelected(value)) {
    return;
  }
  emit("update-selected", value);
}

function focusOption(value) {
  const buttons = root.value?.querySelectorAll(".sp-radio-card-group__option");
  const index = optionValues.value.indexOf(value);
  buttons?.[index]?.focus();
}

function handleOptionKeydown(event, value) {
  const values = optionValues.value;
  // A single-option radiogroup has nowhere to move: arrow keys would wrap the
  // option onto itself and emit a redundant update. No-op instead.
  if (values.length <= 1) {
    return;
  }

  const currentIndex = values.indexOf(value);

  if (event.key === "ArrowDown" || event.key === "ArrowRight") {
    event.preventDefault();
    const next = values[(currentIndex + 1) % values.length];
    emit("update-selected", next);
    focusOption(next);
  } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
    event.preventDefault();
    const previous = values[(currentIndex - 1 + values.length) % values.length];
    emit("update-selected", previous);
    focusOption(previous);
  }
}
</script>

<style>
/*
 * The public token API is the `--sp-comp-radio-card-group-*` inputs below; each is
 * bridged once here to a short local alias (with its ref/literal fallback chain) so the
 * scoped CSS reads `var(--gap)` rather than repeating the full token + fallback inline.
 */
:host {
  display: block;

  --gap: var(--sp-comp-radio-card-group-gap, var(--sp-ref-spacing-8, 1rem));

  --card-background: var(--sp-comp-radio-card-group-card-background-color, var(--sp-sys-color-surface, #fff));
  --card-border-radius: var(--sp-comp-radio-card-group-card-border-radius, 0.25rem);
  --card-padding: var(--sp-comp-radio-card-group-card-padding, var(--sp-ref-spacing-10, 1.25rem));
  /* No border — the cards read as elevated surfaces defined by shadow only (per Figma). */
  --card-shadow: var(--sp-comp-radio-card-group-card-shadow, var(--sp-sys-elevation-4));

  --header-margin-bottom: var(--sp-ref-spacing-6, 0.75rem);

  --group-name-color: var(--sp-comp-radio-card-group-group-name-color, var(--sp-sys-color-on-surface, #1a1a1a));
  --group-name-font-size: var(--sp-comp-radio-card-group-group-name-font-size, 1.0625rem);
  --group-name-font-weight: var(--sp-comp-radio-card-group-group-name-font-weight, var(--sp-sys-font-weight-bold, 700));

  --group-description-color: var(
    --sp-comp-radio-card-group-group-description-color,
    var(--sp-sys-color-on-surface-variant, #56565c)
  );
  --group-description-font-size: var(--sp-comp-radio-card-group-group-description-font-size, 0.9375rem);
  --group-description-font-weight: var(
    --sp-comp-radio-card-group-group-description-font-weight,
    var(--sp-sys-font-weight-normal, 400)
  );
  --group-description-margin-top: var(--sp-ref-spacing-2, 0.25rem);

  --options-gap: var(--sp-ref-spacing-4, 0.5rem);

  --option-background: var(--sp-comp-radio-card-group-option-background-color, var(--sp-sys-color-surface, #fff));
  --option-border-color: var(
    --sp-comp-radio-card-group-option-border-color,
    var(--sp-sys-color-outline-variant, #e3e3e6)
  );
  --option-border-radius: var(--sp-comp-radio-card-group-option-border-radius, 0.25rem);
  --option-padding-block: var(--sp-comp-radio-card-group-option-padding-block, 0.75rem);
  --option-padding-inline: var(--sp-comp-radio-card-group-option-padding-inline, 1rem);
  --option-gap: var(--sp-comp-radio-card-group-option-gap, 0.625rem);
  --option-label-color: var(--sp-comp-radio-card-group-option-label-color, var(--sp-sys-color-primary));
  --option-label-font-weight: var(
    --sp-comp-radio-card-group-option-label-font-weight,
    var(--sp-sys-font-weight-bold, 700)
  );
  --option-label-font-size: var(--sp-comp-radio-card-group-option-label-font-size, 1rem);

  /* Brand fill for the selected radio-card. */
  --selected-background: var(--sp-comp-radio-card-group-selected-background-color, var(--sp-sys-color-primary));
  --selected-border-color: var(--sp-comp-radio-card-group-selected-border-color, var(--selected-background));
  --selected-label-color: var(--sp-comp-radio-card-group-selected-label-color, var(--sp-sys-color-on-primary, #fff));

  --control-size: var(--sp-comp-radio-card-group-control-size, 1.25rem);
  --control-border-color: var(--sp-comp-radio-card-group-control-border-color, var(--sp-sys-color-outline, #b6b6bd));
}
</style>

<style lang="scss" scoped>
.sp-radio-card-group {
  display: flex;
  flex-direction: column;
  gap: var(--gap);

  &__group {
    background: var(--card-background);
    border-radius: var(--card-border-radius);
    box-shadow: var(--card-shadow);
    padding: var(--card-padding);
  }

  &__header {
    margin-bottom: var(--header-margin-bottom);
  }

  &__group-name {
    margin: 0;
    color: var(--group-name-color);
    font-size: var(--group-name-font-size);
    font-weight: var(--group-name-font-weight);
    line-height: 1.3;
  }

  &__group-description {
    margin: var(--group-description-margin-top) 0 0;
    color: var(--group-description-color);
    font-size: var(--group-description-font-size);
    font-weight: var(--group-description-font-weight);
    line-height: 1.35;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: var(--options-gap);
  }

  &__option {
    display: flex;
    align-items: center;
    gap: var(--option-gap);
    width: 100%;
    box-sizing: border-box;
    margin: 0;
    padding: var(--option-padding-block) var(--option-padding-inline);
    text-align: left;
    background: var(--option-background);
    border: 1px solid var(--option-border-color);
    border-radius: var(--option-border-radius);
    cursor: pointer;
    font: inherit;
    color: inherit;

    &:focus-visible {
      outline: 2px solid var(--selected-background);
      outline-offset: 2px;
    }

    &.--is-selected {
      background: var(--selected-background);
      border-color: var(--selected-border-color);

      .sp-radio-card-group__option-label {
        color: var(--selected-label-color);
      }

      .sp-radio-card-group__control {
        border-color: transparent;
      }

      .sp-radio-card-group__check {
        --fill: var(--selected-label-color);
      }
    }
  }

  &__control {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-sizing: border-box;
    width: var(--control-size);
    height: var(--control-size);
    border: 1px solid var(--control-border-color);
    border-radius: 50%;
  }

  &__check {
    --size: calc(var(--control-size) * 0.85);
  }

  &__option-label {
    color: var(--option-label-color);
    font-weight: var(--option-label-font-weight);
    font-size: var(--option-label-font-size);
    line-height: 1.3;
  }
}
</style>
